import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Configure, InstantSearch, ScrollTo } from "react-instantsearch-dom";
// import { SearchBoxProvided } from "react-instantsearch-core";
import SearchHits from "./search-hits";
import NoSearchHits from "./no-search-hits";
import RefinementListMenuSelect from "../search/refinement-list-menuselect";
import RefinementListChips from "../search/refinement-list-chips";
import Stats from "../search/stats";
import VirtualSearchBox from "../search/virtual-searchbox";
// import ClearRefinements from "../search/clear-refinements";
import Pagination from "../search/pagination";
import StateResults from "../search/state-results";
import HasHits from "../search/has-hits";
import HasNoHits from "../search/has-no-hits";
import { Box, Divider, Grid, SelectChangeEvent, Typography, useTheme } from "@mui/material";

import { searchStateToUrl, urlToSearchState, getParamValue } from "../search/utils.js";
import VirtualToggleRefinement from "../search/virtual-toggle-refinement";
import typesenseClient from "../search/search-client";
import { contentTypes } from "../../helpers/content-types";
import SearchHitsGridSkeleton from "../hits/hits-grid-skeleton";
import { CustomWindow } from "./../../custom.window";
// import DisplayModeToggler from "../search/display-mode-toggler";

import { Autocomplete } from "../search/autocomplete";

import { SearchState } from "./../search/types";
import SortOrder from "./../search/sort-order";

declare let window: CustomWindow;

const indexName: string = typeof window.__indexName === "string" ? window.__indexName : "content_prod";

const sortOrderOptions = [
  {
    label: "Relevans",
    value: "content_rank:desc,_text_match:desc,create_date:desc",
  },
  {
    label: "Senaste",
    value: "create_date:desc,_text_match:desc",
  },
];

const Discovery = (): JSX.Element | null => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();

  const [mainEntryState] = React.useState<boolean>(true);
  // const [displayMode, setDisplayMode] = React.useState("grid");
  const [currentSearchState, setCurrentSearchState] = React.useState<SearchState>(urlToSearchState(location));
  const [sortOrder, setSortOrder] = React.useState<string>(sortOrderOptions[0].value);
  const [initialLoad, setInitialLoad] = React.useState<boolean>(true);

  // const prevSearch = React.useRef<string | undefined>(location.search);

  const searchClient = typesenseClient({
    sort_by: sortOrder,
    query_by: "title,content,content_short,content_type,categories,tags,url,image_caption",
    query_by_weights: "5,4,1,1,1,1,1,1",
    prioritize_exact_match: false,
  });

  // Sync state when location changes
  React.useEffect(() => {
    // console.log("#1----------------------------- useEffect");
    //if (prevSearch.current !== location.search) {
    // console.log("#2----------------------------- useEffect");
    //   prevSearch.current = location.search;
    setCurrentSearchState(urlToSearchState(location));
    // }
    // prevSearch.current = location.search;
  }, [location]);

  // Sort menu items in alphabetical order
  const sortItemsDesc = (items: any) => {
    return items
      .map((item: { label: string }) => {
        // Add prefix to content types
        const label = contentTypes.articles.types.indexOf(item.label) >= 0 ? `${contentTypes.articles.prefixName}: ${item.label}` : item.label;
        item.label = label;
        return item;
      })
      .sort((itemA: { label: string }, itemB: { label: string }) => itemA.label.localeCompare(itemB.label, "sv", { sensitivity: "base" }));
  };

  /**
   * Event handlers
   * ===========================================================
   */
  const onSearchStateChange = (updatedSearchState: any) => {
    let state = { ...updatedSearchState, page: updatedSearchState.page, _page: updatedSearchState.page };
    // On initial load, use page from location
    if (initialLoad) {
      state = { ...updatedSearchState, page: currentSearchState.page, query: currentSearchState.query };
      setInitialLoad(false);
    }
    setCurrentSearchState(state);
    history.push(searchStateToUrl(location.pathname, state));
  };

  // Submit search terms
  const onSubmitCallback = (state: any) => {
    const newState = {
      ...currentSearchState,
      query: state.query.trim(),
      page: 1, // On a new search, always start from page 1 as the number of hits isn't known yet
    };
    setCurrentSearchState({
      ...newState,
    });
    history.push(searchStateToUrl(location.pathname, newState));
  };

  // Reset search terms
  const onResetCallback = (query: string) => {
    const newState = {
      ...currentSearchState,
      query,
      page: 1,
    };
    setCurrentSearchState({
      ...newState,
    });
    history.push(searchStateToUrl(location.pathname, newState));
  };

  // Sort order
  const onChangeSortOrderCallback = (event: SelectChangeEvent) => {
    const val = event?.target?.value;
    if (val) {
      setSortOrder(val);
    }
  };

  const hasRefinements = () => {
    if (!currentSearchState?.refinementList) {
      return false;
    }

    if (currentSearchState.refinementList?.categories && currentSearchState.refinementList.categories.length) {
      return true;
    } else if (currentSearchState.refinementList?.types && currentSearchState.refinementList.types.length) {
      return true;
    } else if (currentSearchState.refinementList?.tags && currentSearchState.refinementList.tags.length) {
      return true;
    }
    return false;
  };

  return (
    <InstantSearch indexName={indexName} searchClient={searchClient} searchState={currentSearchState} onSearchStateChange={onSearchStateChange}>
      <Configure hitsPerPage={24} analytics={false} enablePersonalization={false} facetFilters={["main_entry:true"]} />
      <VirtualSearchBox />
      <VirtualToggleRefinement label="" attribute="main_entry" value={mainEntryState} defaultRefinement={true} />
      {currentSearchState && currentSearchState?.page && <ScrollTo />}
      <Box
        p={{ xs: "1rem", lg: "1rem 3rem 1.5rem 3rem" }}
        borderColor="#eee"
        border="1px solid #eee"
        bgcolor="common.white"
        borderRadius={`${theme.shape.borderRadius}px`}
        mt="1.5rem"
        mb="1rem"
      >
        <Grid container alignItems="center" rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" component="h2">
              Sök på webbplatsen
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete initialStateQuery={currentSearchState.query} onSubmitCallback={onSubmitCallback} onResetCallback={onResetCallback} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" component="h2">
              Filtrera
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <RefinementListMenuSelect transformItems={sortItemsDesc} attribute="categories" heading="Huvudområde" size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <RefinementListMenuSelect transformItems={sortItemsDesc} attribute="tags" heading="Ämne" limit={100} size="small" />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <RefinementListMenuSelect transformItems={sortItemsDesc} attribute="types" heading="Utbudstyp" limit={100} size="small" />
          </Grid>
          {/* Spacing in sm layout to make button and stat be on same row */}
          {/* <Grid item sm={6}></Grid>
          <Grid item xs={12} sm={6} md={9}></Grid>
          <Grid item xs={12} sm={6} md={3} textAlign="right">
            <ClearRefinements clearsQuery={true} />
          </Grid> */}
        </Grid>
        {hasRefinements() && (
          <Grid container alignItems="center" rowSpacing={0} columnSpacing={2} sx={{ marginTop: "1rem" }}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" component="h2">
                Valda filter
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ paddingTop: 0 }}>
              <RefinementListChips transformItems={sortItemsDesc} limit={1000} attribute="categories" />
              <RefinementListChips transformItems={sortItemsDesc} limit={1000} attribute="tags" />
              <RefinementListChips transformItems={sortItemsDesc} limit={1000} attribute="types" />
            </Grid>
          </Grid>
        )}
      </Box>

      {/* <DisplayModeToggler callback={setDisplayMode} /> */}

      <Box display="flex" justifyContent="space-between" alignItems="center" pt="2rem">
        <Box width="100%">
          <Stats />
          <Divider />
          <br />
        </Box>
        <HasHits>
          <Box pl="2rem" pr="0.5rem" whiteSpace="nowrap">
            Sortera efter
          </Box>
          <Box>
            <SortOrder size="small" value={sortOrder} options={sortOrderOptions} onChangeCallback={onChangeSortOrderCallback} />
          </Box>
        </HasHits>
      </Box>

      <StateResults fallback={<SearchHitsGridSkeleton />}>
        <HasHits>
          <SearchHits page={currentSearchState.page} />
        </HasHits>
        <HasNoHits>
          <NoSearchHits />
        </HasNoHits>
      </StateResults>
      <HasHits>
        <Pagination page={currentSearchState.page} />
      </HasHits>
    </InstantSearch>
  );
};

export default Discovery;
